
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, UserPlus, Trash2 } from 'lucide-react';
import { AdminUser } from '@/types/admin';


export default function AdminUserManager() {
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  
  const loadAdminUsers = async () => {
    try {
      setIsLoading(true);
      
      // Use a direct select query since we've disabled RLS on the admin_users table
      const { data: admins, error } = await supabase
        .from('admin_users')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Get user details for each admin
      const adminsWithDetails = await Promise.all(
        (admins || []).map(async (admin) => {
          // Try to get user details from auth.users via a function or RPC
          // This is just a placeholder - in production you would need a secure way to fetch this
          // such as an edge function or server-side API
          return {
            ...admin,
            email: "User ID: " + admin.user_id.substring(0, 8) + "..."
          };
        })
      );
      
      setAdminUsers(adminsWithDetails);
    } catch (error) {
      console.error("Error loading admin users:", error);
      toast.error("Failed to load admin users");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAdminUsers();
  }, []);

  const addAdminUser = async () => {
    if (!newAdminEmail) {
      toast.error("Please enter a user ID");
      return;
    }
    
    try {
      setIsAdding(true);
      
      // In a real implementation, you would first lookup the user by email
      // and then get their ID. This requires a secure edge function.
      // For this example, we assume the admin will input the user ID directly
      
      const userId = newAdminEmail.trim();
      
      const { data, error } = await supabase
        .from('admin_users')
        .insert([{ user_id: userId }]);
      
      if (error) throw error;
      
      toast.success("Admin user added successfully");
      setNewAdminEmail('');
      loadAdminUsers();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to add admin user";
      console.error("Error adding admin user:", error);
      toast.error(errorMessage);
    } finally {
      setIsAdding(false);
    }
  };

  const removeAdminUser = async (userId: string) => {
    if (!confirm("Are you sure you want to remove this admin user?")) {
      return;
    }
    
    try {
      const { error } = await supabase
        .from('admin_users')
        .delete()
        .eq('user_id', userId);
      
      if (error) throw error;
      
      toast.success("Admin user removed successfully");
      loadAdminUsers();
    } catch (error) {
      console.error("Error removing admin user:", error);
      toast.error("Failed to remove admin user");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Manage Admin Users</CardTitle>
        <CardDescription>Add or remove admin users</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4">
          <div className="flex gap-2">
            <Input 
              placeholder="Enter user ID" 
              value={newAdminEmail}
              onChange={(e) => setNewAdminEmail(e.target.value)}
              disabled={isAdding}
            />
            <Button 
              onClick={addAdminUser} 
              disabled={isAdding}
            >
              {isAdding ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4 mr-2" />
              )}
              Add Admin
            </Button>
          </div>
          
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-8 w-8 animate-spin text-grandpal-green" />
            </div>
          ) : (
            <div className="border rounded-md">
              <div className="px-4 py-3 border-b bg-muted font-medium">
                Current Admin Users
              </div>
              <div className="divide-y">
                {adminUsers.length === 0 ? (
                  <div className="px-4 py-3 text-center text-muted-foreground">
                    No admin users found
                  </div>
                ) : (
                  adminUsers.map((admin) => (
                    <div 
                      key={admin.user_id} 
                      className="px-4 py-3 flex justify-between items-center"
                    >
                      <div>
                        <div className="font-medium">{admin.email}</div>
                        <div className="text-sm text-muted-foreground">
                          Added: {new Date(admin.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => removeAdminUser(admin.user_id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
